import * as React from "react";
import {authorize, IWithRouterProps, Overlay, withRouter} from "@athena-portal/components";
import {PluginTemplates} from "../../../../../utils/PluginTemplates";
import FalconHeader from "../../../../../components/navigation/FalconHeader";
import {Link} from "react-router-dom";
import {getAmountWithDecimalDigits, setSessionStorage} from "../../../../../CommonFunctions";
import {
    AnnualPolicyPremiumTypeEnum,
    CardDetails,
    ICacheManager,
    MAIN_ANNUAL_PAYMENT_CARD,
    PaymentCard,
    Policy,
    PolicyProperty,
    productHelper,
    Types
} from "@ferry/journey-services";
import {FalconNavigatorHeader, Footer} from "@ferry/portal-core";
import {lazyInject} from "@athena-portal/core";
import {OutstandingPaymentForm} from "./OutstandingPaymentForm";
import {PanelDashed} from "../../../../../components/PanelDashed";
import {FailedPaymentMessage} from "../../../../../components/payments/FailedPaymentMessage";
import type {ICustomerPortalService} from "../../../../../services/ICustomerPortalService";
import {Types as CustomerPortalTypes} from "../../../../../Types";

interface IParams {
    policyNumber: string
    invoiceId: string
    [key: string]: string | undefined
}

interface IState {
    policy?: Policy,
    allowToUseCard?: boolean
}

@authorize
export class OutstandingPaymentPagePlain extends React.Component<IWithRouterProps<IParams>, IState> {
    @lazyInject(CustomerPortalTypes.CustomerPortalService)
    private customerPortalService: ICustomerPortalService;

    @lazyInject(Types.CacheManager)
    private cacheManager: ICacheManager;

    private pluginTemplates = new PluginTemplates();

    constructor(props: IWithRouterProps<IParams>) {
        super(props);

        this.setPaymentAmount = this.setPaymentAmount.bind(this);

        this.state = {};
    }

    public componentDidMount() {
        this.pluginTemplates.setPageTitle("Payment");
        setSessionStorage("isChargeBack", true);
        setSessionStorage("missedPaymentPolicyNumber", this.props.params.policyNumber);
        this.cacheManager.clearCache("getPolicyHistory");

        const policyNumber: string = this.props.params.policyNumber;

        this.customerPortalService
            .getPolicy(policyNumber)
            .subscribe((policy: Policy) => {
                const properties: PolicyProperty[] = policy.policyProperties.filter(pp => pp.key === MAIN_ANNUAL_PAYMENT_CARD);
                const mainPaymentCard: PolicyProperty | undefined = properties.length > 0 ? properties[0] : undefined;

                if (mainPaymentCard && policy.annualPolicyInfo && policy.annualPolicyInfo.paymentCards) {
                    const mainCards: PaymentCard[] = policy.annualPolicyInfo.paymentCards.filter(c => c.id.toString() === mainPaymentCard.value);

                    if (mainCards) {
                        this.setState({
                            allowToUseCard: mainCards[0].reuseCard
                        })
                    }
                }

                this.setState({
                    policy
                })
            });
    }

    public render() {
        const dataLoaded = this.state.policy;
        return (
            <React.Fragment>
                <Overlay show={!dataLoaded}/>
                <FalconHeader/>
                {dataLoaded && this.renderPageContents()}
                <Footer/>
            </React.Fragment>
        );
    }

    private readonly getRedirectUrl = (): string => {
        return this.state.policy?.annualPolicyInfo.premiumType === AnnualPolicyPremiumTypeEnum.MONTHLY
            ? `/dashboard/monthlyPaymentHistory/${this.props.params.policyNumber}`
            : `/dashboard/annualPaymentHistory/${this.props.params.policyNumber}`;
    }

    private renderPageContents() {
        if (this.state.allowToUseCard) {
            window.sessionStorage.setItem("paymentAmount", getAmountWithDecimalDigits(this.getAmountToPay()).toString());
            return (
                <div className="plugin">
                    {this.props.location?.state?.verifyCardFailed && <FailedPaymentMessage/>}
                    <FalconNavigatorHeader back={this.getRedirectUrl()} title="Pay off my outstanding balance"/>
                    <div className="panel">
                        <div
                            className="currency-amount-display price font-family-display font-weight-bold text-center">
                            {getAmountWithDecimalDigits(this.getAmountToPay())}
                        </div>
                        <div>
                            <p className="text-center font-weight-bold mb-0">to pay for outstanding balance</p>
                            <p className="text-center pt-2">All figures include Insurance Premium Tax</p>
                        </div>
                    </div>
                    <PanelDashed className="shadow bg-white text-center pay-by-card-panel">
                        <div className="panel-dashed-contents card-information">{this.renderCardInformation()}</div>
                        <div className="pb-2 or-divider"> OR</div>
                        <Link id="payNow"
                                className="btn btn-white-dashboard mt-0 mb-0"
                                aria-label={"Pay Now"}
                                onClick={(event: any) => this.handleUseNewCard(event)}
                                to={'#'}>
                            Use a different card
                        </Link>
                        <div className="panel-dashed-contents my-3 card-buttons">{this.renderCardsButtons()}</div>
                    </PanelDashed>
                    <button type="button"
                            className="btn btn-default btn-white-dashboard m-auto"
                            onClick={this.setPaymentAmount}>
                        Cancel changes
                    </button>
                    {this.pluginTemplates.renderBackReloadLinkWithAdditionalFunctionality("Back", true, this.getRedirectUrl(), this.setPaymentAmount)}
                </div>
            );
        } else {
            return (
                <div className="plugin">
                    <OutstandingPaymentForm
                        verifyNewCardFailed={false}
                        invoiceId={this.props.params.invoiceId}
                        policyNumber={this.props.params.policyNumber}
                    />
                </div>
            );
        }
    }

    private readonly renderCardInformation = () => {
        const card: CardDetails | null = this.findMainPaymentCard();
        if (card) {
            return (
                <div className="d-flex justify-content-center mt-3">
                    <div className="card-details-wrapper">
                        <span className="pr-2">{this.renderCardImage(card)}</span>&nbsp;
                        <span>{this.renderCardTypeName(card)}</span>&nbsp;
                        <span>ending in <b>{card.number.split('******')[1]}</b></span>
                    </div>
                </div>
            );
        } else {
            return <React.Fragment/>
        }
    };

    private readonly renderCardImage = (card: CardDetails) => {
        if (card.type.includes("VISA")) {
            return <img className="payment-icon" alt="Visa icon" src="/img/visa-icon.svg"/>;
        }
        if (card.type.includes("MASTERCARD")) {
            return <img className="payment-icon" alt="Mastercard icon" src="/img/mastercard-icon.svg"/>;
        }
        return <React.Fragment/>;
    };

    private readonly renderCardTypeName = (card: CardDetails): string => {
        if (card.type.includes("VISA")) {
            return "Visa";
        }
        if (card.type.includes("MASTERCARD")) {
            return "Mastercard";
        }
        return "";
    };

    private findMainPaymentCard(): CardDetails | null {
        let card = {} as CardDetails;
        const {policy} = this.state;
        if (policy?.interaction && productHelper.isWolfMotor(policy?.interaction) &&
            policy?.interaction.policyHolder.cardDetails.length !== 0) {
            card = policy?.interaction.policyHolder.cardDetails[0];
            return card;
        }
        return null
    }

    private readonly renderCardsButtons = () => {
        const cardsButtons: any = [];
        const amountToPay: number = this.getAmountToPay();
        const {policy} = this.state;
        if (policy?.interaction.policyHolder.cardDetails) {
            for (const cardDetails of policy?.interaction.policyHolder.cardDetails) {
                cardsButtons.push(
                    <button key={cardDetails.number}
                            className="btn btn-primary btn-make-payment m-auto margin-top-17 justify-content-center"
                            onClick={(event: any) => this.handleOnClick(event)}
                            id={"card-" + cardDetails.number}
                            aria-label={`${getAmountWithDecimalDigits(amountToPay)} to pay`}
                    >
                        {`Pay ${getAmountWithDecimalDigits(amountToPay)}`}
                        <span className="payment-lock-image"></span>
                    </button>
                );
            }
        }
        return cardsButtons;
    };

    private getAmountToPay = (): number => {
        const unpaidChargebackInvoiceInfo: any = this.state.policy?.annualPolicyInfo.unpaidChargebackInvoiceInfo;
        return unpaidChargebackInvoiceInfo?.[this.props.params.invoiceId];
    }

    private handleOnClick(event: any): void {
        window.location.assign(`/dashboard/account/paymentProcessing/${null}/${this.state.policy?.policyNumber}/${this.props.params.invoiceId}`);
        event.preventDefault();
    }

    private handleUseNewCard(event: any): void {
        this.setState({
            allowToUseCard: false
        })
    }

    private setPaymentAmount(event: any): void {
        window.sessionStorage.removeItem("paymentAmount");
        window.location.href = this.getRedirectUrl();
    }
}

export const OutstandingPaymentPage = withRouter<{}, IParams>(OutstandingPaymentPagePlain)
